import { useMemo } from 'react';

import {
  AddIcon,
  Button,
  CopyIcon,
  Divider,
  DownloadIcon,
  ExcelIcon,
  type TypeSelectOption,
  dialogService,
} from '@thefarmersfront/kitchen';

import { Table, TableBottomField, TableTopField } from '~/components/common';

import { useStableCallback } from '~/hooks/common';
import { useUserInfoStore } from '~/store';
import { getReceiveRegisterColumnDefs } from '~/utils/receive/table';

import { RECEIVE_PAGE_SIZE } from '~/constants/receive';

import type { TypePagination } from '~/types/common.types';
import type { TypeReceiveRequestsInfo } from '~/types/receive.types';

import styles from './ReceiveRequestTable.module.scss';

const getRequestRowId = (row: TypeReceiveRequestsInfo) => String(row.requestId);

const ReceiveRequestTable = ({
  data,
  pagination,
  isDownloadReady,
  selectedItems,
  onChangePageLimit,
  onChangePage,
  onChangeRowSelection,
  onClickViewDetail,
  onClickSpecificationDownload,
  onClickExcelDownload,
  onClickRequestCancel,
  onClickRequestCopy,
  onClickRequestRegister,
  onClickPrintReceivingLabel,
}: {
  data: TypeReceiveRequestsInfo[] | null;
  pagination: TypePagination;
  isDownloadReady: boolean;
  selectedItems: TypeReceiveRequestsInfo[];
  onChangePageLimit: (value: TypeSelectOption<number>) => void;
  onChangePage: (value: number) => void;
  onChangeRowSelection: (selectedRows: TypeReceiveRequestsInfo[]) => void;
  onClickViewDetail: (data?: TypeReceiveRequestsInfo) => void;
  onClickSpecificationDownload: () => void;
  onClickExcelDownload: () => void;
  onClickRequestCancel: () => void;
  onClickRequestCopy: () => void;
  onClickRequestRegister: () => void;
  onClickPrintReceivingLabel: () => void;
}) => {
  const hasPermission = useUserInfoStore((state) => state.hasPermission);
  const hasPermissionNFA = hasPermission([{ ownerType: 'NFA' }]);

  // 부모 핸들러가 렌더마다 새 함수여도 컬럼(셀 함수)을 다시 만들지 않는다.
  const handleClickViewDetail = useStableCallback(onClickViewDetail);
  const { columnDefs } = useMemo(
    () => getReceiveRegisterColumnDefs({ onClickViewDetail: handleClickViewDetail }),
    [handleClickViewDetail]
  );

  const selectedRowIds = useMemo(() => selectedItems.map(getRequestRowId), [selectedItems]);

  return (
    <div className={styles['table-container']}>
      <TableTopField
        dataTotalCount={pagination.totalCount}
        rightButton={
          <div className={styles['table-top-right-field']}>
            <Button
              size="tiny"
              variant="outlined"
              color="tertiary"
              leftIcon={<DownloadIcon size={16} />}
              disabled={selectedItems.length === 0}
              onClick={onClickSpecificationDownload}
            >
              검수확인용 거래명세서
            </Button>
            <Divider className={styles['divider']} orientation="vertical" color="gray100" />
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              disabled={!isDownloadReady}
              leftIcon={<ExcelIcon size={16} />}
              onClick={onClickExcelDownload}
            >
              엑셀 전체 다운로드
            </Button>
            <Divider className={styles['divider']} orientation="vertical" color="gray100" />
            <Button
              size="tiny"
              variant="outlined"
              color="secondary"
              disabled={selectedItems.length === 0}
              onClick={onClickPrintReceivingLabel}
            >
              입하 라벨 출력
            </Button>
            <Button
              size="tiny"
              variant="outlined"
              status="error"
              disabled={selectedItems.length === 0}
              onClick={onClickRequestCancel}
            >
              요청 취소
            </Button>
            <Button
              size="tiny"
              variant="outlined"
              color="secondary"
              leftIcon={<CopyIcon size={16} />}
              disabled={selectedItems.length === 0}
              onClick={() => {
                if (hasPermissionNFA) {
                  dialogService.addDialog({
                    children: '네이버 스마트스토어를 통해서만\n입고 요청 가능합니다.',
                  });
                  return;
                }
                onClickRequestCopy();
              }}
            >
              입고 요청 복사
            </Button>
            <Button
              size="tiny"
              color="secondary"
              leftIcon={<AddIcon size={16} />}
              onClick={() => {
                if (hasPermissionNFA) {
                  dialogService.addDialog({
                    children: '네이버 스마트스토어를 통해서만\n입고 요청 가능합니다.',
                  });
                  return;
                }
                onClickRequestRegister();
              }}
            >
              입고 요청 등록
            </Button>
          </div>
        }
      />
      <Table
        className={styles['table']}
        data={data}
        columns={columnDefs}
        rowStatusType={data === null ? 'INIT' : 'EMPTY'}
        getRowId={getRequestRowId}
        selection={{ selectedRowIds, onChange: onChangeRowSelection }}
      />
      <TableBottomField
        pageSizeOptions={Object.values(RECEIVE_PAGE_SIZE)}
        currentPage={pagination.currentPage}
        totalCount={pagination.totalCount}
        pageSize={pagination.pageSize}
        onChangePageLimit={onChangePageLimit}
        onClickPage={onChangePage}
      />
    </div>
  );
};

export default ReceiveRequestTable;
